import { Link } from 'gatsby';
import type { FC, PropsWithChildren } from 'react';
import React, { use } from 'react';

import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { useClientSnapshot } from 'src/custom-hooks/use-client-snapshot';
import { useCrossClubSharedSummary } from 'src/custom-hooks/use-cross-club-shared-summary';
import type { ClubInformation } from 'src/enums/club-information';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { getImageForClubFinder } from 'src/helpers/hosted-asset-helpers';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import {
  getAbsoluteClubPagePathFromClubInformation,
  getClubPagePathFromClubInformation,
} from 'src/helpers/navigation-helper';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';
import { trimLocationString } from 'src/helpers/string-helper';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import * as styles from './recently-visited-club-info.module.scss';

const getRecentlyViewedSearchClubId = () =>
  getLocalStorageItem(RecentlyViewedClubStorageKey.searchClubId) ?? null;

export const RecentlyVisitedClubInfo: FC = () => {
  const clubId = useClientSnapshot(getRecentlyViewedSearchClubId, null);

  return !!clubId && <RecentlyVisitedClubInfoContent clubId={clubId} />;
};

const RecentlyVisitedClubInfoContent: FC<{ clubId: string }> = ({ clubId }) => {
  const { buildType, language } = use(SiteContext);
  const { headerContent } = use(GlobalCmsContent);
  const clubInformation = getClubInformation(clubId);
  const clubName = clubInformation.clubName;

  const shared = useCrossClubSharedSummary(clubId);
  const phone = shared.phoneNumbers.nonMembersPhoneNumber;

  if (!headerContent?.searchSettings?.recentlyViewedClubCaption) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainTitle}>
        {headerContent.searchSettings.recentlyViewedClubCaption}
      </div>
      <div className={styles.wrapper}>
        <LinkComponent clubInformation={clubInformation}>
          <img
            src={getImageForClubFinder(shared.clubThumbnailImage)}
            alt={clubName}
            className={styles.image}
          />
        </LinkComponent>
        <div className={styles.textWrapper}>
          <LinkComponent clubInformation={clubInformation}>
            <span className={styles.title}>
              {clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language)}
              {shared.comingSoonClubFinder && (
                <span className={styles.comingSoon}>
                  {shared.statusClubFinder ? (
                    shared.statusClubFinder
                  ) : (
                    <TranslatedStaticText lookupKey={'comingSoon'} />
                  )}
                </span>
              )}
            </span>
            <div className={styles.address}>
              {trimLocationString(shared.clubLocation.text1!, shared.clubLocation.text2!) ?? ''}
            </div>
          </LinkComponent>
          <a
            className={styles.link}
            href={`tel:${phone}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {phone}
          </a>
        </div>
      </div>
    </div>
  );
};

const LinkComponent: FC<PropsWithChildren<{ clubInformation: ClubInformation }>> = ({
  clubInformation,
  children,
}) => {
  const { buildType, language } = use(SiteContext);
  return !!clubInformation && clubInformation.buildType === buildType ? (
    <Link to={getClubPagePathFromClubInformation('', clubInformation, language)}>{children}</Link>
  ) : (
    <a href={getAbsoluteClubPagePathFromClubInformation('', clubInformation, language)}>
      {children}
    </a>
  );
};
